import * as React from 'react';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { Text } from '@/hen-ui/atomics/Text';
import { cn } from '@/hen-ui/utils';
import { priceVariants } from './price.variants';

export type PriceSize = 'sm' | 'md' | 'lg';

const TEXT_VARIANT_BY_SIZE = {
  sm: 'b5',
  md: 'b4',
  lg: 'b3',
} as const satisfies Record<PriceSize, 'b5' | 'b4' | 'b3'>;

export interface PriceProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> {
  /**
   * Current price shown prominently (e.g. `"$120"`).
   * Pass a pre-formatted string — this component does not localize or format currency.
   */
  price: string;
  /**
   * Optional former price rendered with strikethrough after `price`
   * (e.g. `"$150"`). Omitted or blank when there is no comparison.
   */
  originalPrice?: string;
  /** Typography scale. Defaults to `"md"` (body b4). */
  size?: PriceSize;
}

/**
 * Price - Inline current / former price pair
 *
 * Displays a current price with an optional strikethrough original price.
 * UI-only: formatting and discount logic stay with the consumer.
 *
 * @example
 * <Price price="$120" />
 *
 * @example
 * <Price price="$120" originalPrice="$150" />
 */
export const Price = React.forwardRef<HTMLSpanElement, PriceProps>(
  (
    {
      price,
      originalPrice,
      size = 'md',
      className,
      'aria-label': ariaLabel,
      ...props
    }: PriceProps,
    ref,
  ) => {
    const textVariant = TEXT_VARIANT_BY_SIZE[size];
    const showOriginal =
      originalPrice != null && String(originalPrice).trim() !== '';
    const groupAriaLabel =
      ariaLabel ??
      (showOriginal
        ? `Current price ${price}, original price ${originalPrice}`
        : undefined);

    return (
      <span
        ref={ref}
        data-hen-id={COMPONENT_IDS.Price}
        data-size={size}
        className={cn(priceVariants(), className)}
        {...props}
        role={showOriginal ? 'group' : props.role}
        aria-label={groupAriaLabel}
      >
        <Text variant={textVariant} weight='bold' tag='span'>
          {price}
        </Text>
        {showOriginal && (
          <Text
            variant={textVariant}
            color='subtext'
            tag='del'
            className='line-through'
          >
            {originalPrice}
          </Text>
        )}
      </span>
    );
  },
);

Price.displayName = 'Price';
